// Sign in with Apple on the website.
//
// Apple's own script opens a popup and hands back an identity token, the same
// kind the iOS app gets, and the backend's POST /auth/apple checks both the
// same way. Needs a Services ID set up in the Apple Developer portal, with
// this site's domain and return URL registered; until REACT_APP_APPLE_SERVICES_ID
// is set, the button is not shown at all.
//
// The nonce: Apple is given its SHA-256, the backend the original, so a token
// taken from somewhere else cannot be replayed without it.

const APPLE_SCRIPT =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || '';
export const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== 'undefined' ? `${window.location.origin}/login` : '');

export const appleSignInAvailable = () => Boolean(APPLE_SERVICES_ID);

let scriptPromise = null;

/** Loads Apple's script once. Called on page load so the click can open the
 * popup straight away: a popup opened after an await is often blocked. */
export function loadAppleScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.AppleID) return Promise.resolve(window.AppleID);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = APPLE_SCRIPT;
      script.async = true;
      script.onload = () => resolve(window.AppleID);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Could not load Sign in with Apple'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export function makeNonce() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** Lowercase hex SHA-256, as the backend's apple_auth.nonce_hash computes it. */
export async function sha256Hex(text) {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** A nonce and its hash, prepared before the click. */
export async function prepareAppleSignIn() {
  const AppleID = await loadAppleScript();
  const nonce = makeNonce();
  AppleID.auth.init({
    clientId: APPLE_SERVICES_ID,
    scope: 'name email',
    redirectURI: APPLE_REDIRECT_URI,
    nonce: await sha256Hex(nonce),
    usePopup: true,
  });
  return { AppleID, nonce };
}

/**
 * Opens Apple's popup. Resolves with what the backend needs, or null if the
 * person closed the popup (a choice, not an error).
 */
export async function signInWithApple(prepared) {
  try {
    const result = await prepared.AppleID.auth.signIn();
    const name = result.user?.name;
    const fullName = name ? [name.firstName, name.lastName].filter(Boolean).join(' ') : null;
    return {
      identityToken: result.authorization.id_token,
      // Needed only to delete an account: the backend exchanges it to
      // revoke Petlings from the person's Apple ID.
      authorizationCode: result.authorization.code,
      nonce: prepared.nonce,
      fullName: fullName || null,
    };
  } catch (err) {
    if (err?.error === 'popup_closed_by_user' || err?.error === 'user_cancelled_authorize') {
      return null;
    }
    throw err;
  }
}
